import React from 'react';
import {
  deleteAccessGroup,
  deleteUserData,
  getAccessGroups,
  getOrgData,
  getOrgList,
  getOrgSettings,
  getUserData,
  patchAccessGroup,
  patchOrganization,
  patchUserData,
  postAccessGroup,
  postOrganization,
  postOrgSettings,
  postUserData,
} from 'utils/fetchers';
import { omit } from 'utils/index';
import { localStorageGet, localStorageSave } from 'utils/localStorage';

export interface IEditingContext {
  editJSON: (org: string, data: string) => void;
  data: Record<string, IOrg>; // org: settings

  loading: 'fetch' | 'save' | null;
  selectOrg: (orgId: string | null) => void;
  submitNewData: (orgId: string, data: Partial<IOrg>) => Promise<boolean> | null;
  submitOrgSettingsJSON: (orgId: string, application: string, json: string) => Promise<boolean> | null;

  postOrganizationData: (orgId: string, data: IOrganizationFormData) => Promise<boolean> | null;
}

export const EditingContext = React.createContext<IEditingContext>({
  editJSON: () => '',
  data: {},
  loading: null,
  submitNewData: () => null,
  selectOrg: () => '',
  submitOrgSettingsJSON: () => null,
  postOrganizationData: () => null,
});

export const EditingContextPrimed = ({ children }: IEditingContextPrimed) => {
  const [loading, setLoading] = React.useState<IEditingContext['loading']>(null);
  const [selectedOrg, setSelectedOrg] = React.useState<string | null>(null);

  const [allOrgsData, setAllOrgsData] = React.useState<Record<string, IOrg>>({});

  const editDataInStorageJSON = (orgId: string, data: string) => {
    const localStorageEditData = localStorageGet('editData');
    const editDataOld = localStorageEditData ? JSON.parse(localStorageEditData) : {};
    const newData = { ...editDataOld, [orgId]: data };
    localStorageSave('editData', JSON.stringify(newData));
  };

  const submitNewData = (orgId: string, newData: Partial<IOrg>) => {
    const promises: Array<Promise<boolean | null>> = [];

    if (newData) {
      setLoading('save');

      const { orgSettings, users, accessGroups, ...rest } = newData;

      if (rest && Object.keys(rest).length > 0) {
        const formattedData = {
          ...(rest.platforms
            ? {
                platforms: rest.platforms,
              }
            : {}),
          ...(rest.externalAdmissionGroups
            ? {
                externalAdmissionGroups:
                  typeof rest.externalAdmissionGroups === 'string'
                    ? (rest.externalAdmissionGroups as string).split(/,\s*/)
                    : rest.externalAdmissionGroups,
              }
            : {}),
          ...(rest.autocreateAlternateIdTypes
            ? {
                autocreateAlternateIdTypes:
                  typeof rest.autocreateAlternateIdTypes === 'string'
                    ? (rest.autocreateAlternateIdTypes as string).split(/,\s*/)
                    : rest.autocreateAlternateIdTypes,
              }
            : {}),
        };
        promises.push(patchOrganization(orgId, { ...rest, ...formattedData }));
      }

      if (orgSettings) {
        // Org settings at this time should be entered using json
      }

      if (Array.isArray(users)) {
        users.forEach(async user => {
          switch (user.action) {
            case 'new': {
              const data = omit<TVaultUser, 'action'>(['action'], user);
              promises.push(postUserData(data, selectedOrg));
              break;
            }
            case 'remove': {
              promises.push(deleteUserData(user.userId, selectedOrg));
              break;
            }
            case 'edit': {
              const data = omit<TVaultUser, 'action'>(['action'], user);
              promises.push(patchUserData(data.userId, data, selectedOrg));
              break;
            }
            default: {
              break;
            }
          }
        });
      }

      if (Array.isArray(accessGroups)) {
        accessGroups.forEach(async group => {
          switch (group.action) {
            case 'new': {
              const data = omit<TVaultAccessGroup, 'action'>(['action'], group);
              promises.push(postAccessGroup(data, selectedOrg));
              break;
            }
            case 'remove': {
              promises.push(deleteAccessGroup(group.groupId, selectedOrg));
              break;
            }
            case 'edit': {
              const data = omit<TVaultAccessGroup, 'action'>(['action'], group);
              promises.push(patchAccessGroup(data.groupId, data, selectedOrg));
              break;
            }
            default: {
              break;
            }
          }
        });
      }
    } else return null;

    return Promise.all(promises)
      .then(() => {
        getOrganizationDatas();
        setLoading(null);
        return true;
      })
      .catch(() => {
        setLoading(null);
        return false;
      });
  };

  const submitOrgSettingsJSON = async (orgId: string, application: string, json: string) => {
    setLoading('save');
    return await postOrgSettings(orgId, application, json)
      .then(res => {
        if (res) {
          getOrganizationDatas();
          setLoading(null);
          return true;
        }
        throw null;
      })
      .catch(() => {
        setLoading(null);
        return false;
      });
  };

  const getOrganizations = () => {
    setLoading('fetch');
    getOrgList().then(data => {
      const orgObj = {};
      (data || []).forEach(org => {
        Object.assign(orgObj, { [org.orgId]: org });
      });

      localStorageSave('orgData', JSON.stringify(orgObj));
      setLoading(null);
    });
  };

  const getOrganizationDatas = () => {
    if (selectedOrg) {
      setLoading('fetch');
      const fetches = [
        getOrgData(selectedOrg),
        getOrgSettings(selectedOrg),
        getUserData(selectedOrg),
        getAccessGroups(selectedOrg),
      ];

      Promise.all(fetches).then(([orgData, orgSettings, users, accessGroups]) => {
        const lsData = localStorageGet('orgData');
        const parsed = lsData ? JSON.parse(lsData) : {};
        const thisOrgData = parsed[selectedOrg];
        let thisOrgNewData = thisOrgData;
        if (orgData) {
          thisOrgNewData = {
            ...thisOrgNewData,
            ...orgData,
          };
        }
        if (orgSettings) {
          thisOrgNewData = {
            ...thisOrgNewData,
            orgSettings: orgSettings,
          };
        }
        if (users) {
          thisOrgNewData = { ...thisOrgNewData, users: users };
        }
        if (accessGroups) {
          thisOrgNewData = { ...thisOrgNewData, accessGroups: accessGroups };
        }
        localStorageSave('orgData', JSON.stringify({ ...parsed, [selectedOrg]: thisOrgNewData }));
        setLoading(null);
      });
    }
  };

  const postOrganizationData = async (orgId: string, data: IOrganizationFormData, patch = false) => {
    const orgData: Omit<TVaultOrganization, 'orgId'> = {
      displayName: data.displayName || '',
      orgGroup: data.orgGroup || '',
      platforms: data.platforms || [],
      externalAdmissionGroups: data.externalAdmissionGroups ? data.externalAdmissionGroups.split(/,\s*/) : null,
      autocreateAlternateIdTypes: data.autocreateAlternateIdTypes
        ? data.autocreateAlternateIdTypes.split(/,\s*/)
        : null,
    };

    const fetcher = patch ? patchOrganization : postOrganization;

    setLoading('save');
    return await fetcher(orgId, orgData).then(res => {
      if (res) {
        getOrganizations();
        return true;
      } else {
        setLoading(null);
        return false;
      }
    });
  };

  // Listen to changes in localstorage
  const listener = () => {
    const localStorageOrgData = localStorageGet('orgData');
    const lsAllOrgsData: Record<string, IOrg> = localStorageOrgData ? JSON.parse(localStorageOrgData) : {};
    setAllOrgsData(lsAllOrgsData);
  };

  // Load data at mount
  React.useEffect(() => {
    window.addEventListener('localStorageSave', listener);
    getOrganizations();
    return () => {
      window.removeEventListener('localStorageSave', listener);
    };
  }, []);

  // Load specific organization data when selecting an org
  React.useEffect(() => {
    getOrganizationDatas();
  }, [selectedOrg]);

  return (
    <EditingContext.Provider
      value={{
        editJSON: (org: string, data: string) => editDataInStorageJSON(org, data),
        data: allOrgsData || {},
        loading,
        selectOrg: setSelectedOrg,
        submitNewData,
        submitOrgSettingsJSON,
        postOrganizationData,
      }}
    >
      <EditingContext.Consumer>
        {(/** Can enter context values here and input them straight to child elements */) => children}
      </EditingContext.Consumer>
    </EditingContext.Provider>
  );
};

interface IEditingContextPrimed {
  children: React.JSX.Element | React.JSX.Element[];
}
